/**
 * Tokens, PKCE, and refresh. The whole of D47 lives here.
 *
 * Tokens sit in `localStorage` and the ID token goes to the API in an
 * `Authorization` header. There is no session cookie and no backend-for-
 * frontend: both app clients are public, so there is no secret for a server to
 * hold, and PKCE is what makes the code exchange safe from the browser.
 *
 * **The ID token, not the access token.** It is the only one of the two
 * carrying `aud`, which the API's authorizer checks, and the only one carrying
 * `email`.
 *
 * This warning used to be sharper: the custom `displayName` and `team` claims
 * were ID-token-only, so sending the access token attributed every photograph
 * in the gallery to "Guest" while nothing 401'd. D55 took both off the token —
 * attribution reads the guest row now — so that particular trap is gone. The
 * rule is unchanged and the two tokens are still not interchangeable.
 */
import { AUTH, redirectUri } from './auth-config';

const TOKENS_KEY = 'mv.tokens';
const VERIFIER_KEY = 'mv.pkceVerifier';

export interface Tokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  /** Epoch ms. Cognito gives seconds-from-now; we store an absolute time so a
   *  reload does not think a stale token is fresh. */
  expiresAt: number;
}

export interface Claims {
  sub: string;
  email?: string;
  name?: string;
  /** @deprecated Vestigial since D55 — profile comes from `GET /me`. */
  'custom:displayName'?: string;
  /** @deprecated Vestigial since D55 — profile comes from `GET /me`. */
  'custom:team'?: string;
  'cognito:groups'?: string[];
  identities?: { providerName?: string }[];
  exp: number;
}

export function readTokens(): Tokens | null {
  try {
    const raw = localStorage.getItem(TOKENS_KEY);
    return raw ? (JSON.parse(raw) as Tokens) : null;
  } catch {
    return null;
  }
}

export function writeTokens(t: Tokens | null) {
  if (t) localStorage.setItem(TOKENS_KEY, JSON.stringify(t));
  else localStorage.removeItem(TOKENS_KEY);
}

/** Decode only — the signature is verified by the API's authorizer, which is
 *  the only place a verdict matters. Trusting this client-side would be
 *  meaningless anyway: whoever controls the browser controls what it decodes. */
export function decode(idToken: string): Claims | null {
  try {
    const payload = idToken.split('.')[1];
    if (!payload) return null;
    // TextDecoder rather than `decodeURIComponent(escape(...))`: `escape` is
    // deprecated, and this payload carries guest names — plenty of which will
    // not be ASCII.
    const bytes = Uint8Array.from(atob(payload.replace(/-/g, '+').replace(/_/g, '/')), (ch) => ch.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as Claims;
  } catch {
    return null;
  }
}

// ── PKCE ────────────────────────────────────────────────────────────────────

const b64url = (bytes: ArrayBuffer) =>
  btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return b64url(digest);
}

/**
 * Sends the browser to managed login. Never returns — the page navigates away,
 * so the returned promise is a formality the caller awaits and never sees
 * settle.
 *
 * `idpHint` skips Cognito's provider chooser and goes straight to Google, which
 * is what makes the primary button one tap rather than two (D10).
 */
export async function beginSignIn(idpHint?: 'Google'): Promise<never> {
  const verifier = b64url(crypto.getRandomValues(new Uint8Array(32)).buffer);
  // sessionStorage, not localStorage: the verifier is single-use and scoped to
  // this tab's redirect. Leaving it behind after the exchange is pointless.
  sessionStorage.setItem(VERIFIER_KEY, verifier);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: AUTH.guestClientId,
    redirect_uri: redirectUri(),
    scope: 'openid email profile',
    code_challenge: await challengeFor(verifier),
    code_challenge_method: 'S256',
  });
  if (idpHint) params.set('identity_provider', idpHint);

  window.location.assign(`${AUTH.domain}/oauth2/authorize?${params}`);
  return new Promise<never>(() => {});
}

const store = (r: Record<string, string | number>, fallbackRefresh?: string): Tokens => ({
  idToken: String(r.id_token),
  accessToken: String(r.access_token),
  // A refresh response does not return a refresh token — you keep the one you
  // have. Dropping it here would sign every guest out after an hour.
  refreshToken: String(r.refresh_token ?? fallbackRefresh ?? ''),
  expiresAt: Date.now() + Number(r.expires_in ?? 3600) * 1000,
});

/** Step two: swap the code for tokens, from the browser, using the verifier. */
export async function completeSignIn(code: string): Promise<Tokens> {
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  if (!verifier) throw new Error('no_verifier');
  sessionStorage.removeItem(VERIFIER_KEY);

  const res = await fetch(`${AUTH.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: AUTH.guestClientId,
      code,
      redirect_uri: redirectUri(),
      code_verifier: verifier,
    }),
  });
  if (!res.ok) throw new Error(`token_exchange_${res.status}`);
  const tokens = store(await res.json());
  writeTokens(tokens);
  return tokens;
}

/** Refresh a minute early, so a request never leaves with a token that expires
 *  in flight on a slow connection. */
const SKEW_MS = 60_000;

export async function refresh(existing: Tokens): Promise<Tokens | null> {
  if (!existing.refreshToken) return null;
  const res = await fetch(`${AUTH.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: AUTH.guestClientId,
      refresh_token: existing.refreshToken,
    }),
  });
  if (!res.ok) return null;
  const tokens = store(await res.json(), existing.refreshToken);
  writeTokens(tokens);
  return tokens;
}

/**
 * The ID token to send, refreshing first if it is close to expiry. Returns null
 * when the refresh token has run out too — 30 days for a guest, which outlasts
 * the trip by three weeks.
 */
export async function currentIdToken(): Promise<string | null> {
  let tokens = readTokens();
  if (!tokens) return null;
  if (Date.now() > tokens.expiresAt - SKEW_MS) {
    tokens = await refresh(tokens);
    if (!tokens) { writeTokens(null); return null; }
  }
  return tokens.idToken;
}
